import type { ConsistencyDay } from '@anytracker/core'

import styles from './ConsistencyStrip.module.css'

interface Props {
  /** From core's `consistency`, oldest first. */
  readonly days: readonly ConsistencyDay[]
  /** The same fact in words. Required — see below. */
  readonly caption: string
  /**
   * `band`: Home's full-width section, titled, with the first and last day
   * named under the cells. `bars`: cells and caption on one line. `dots`: the
   * post-save confirmation's two-state row.
   */
  readonly variant?: 'band' | 'bars' | 'dots'
  /** The heading over a `band`, such as "Last 14 days". */
  readonly title?: string
}

/** "22 Oct" from a `YYYY-MM-DD` day key. Read as UTC: the key already names the day. */
function shortDay(day: string): string {
  const parsed = Date.parse(`${day}T12:00:00Z`)
  if (Number.isNaN(parsed)) {
    return day
  }
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(parsed)
}

/**
 * How much was logged each day, lately (T-903).
 *
 * Shared between Home and the post-save confirmation so the two cannot disagree
 * about the same fortnight — the bands come from core and the shape from here,
 * and neither screen owns either.
 *
 * **The caption is not optional.** The strip is `aria-hidden`, because a row of
 * fourteen unlabelled cells is noise to a screen reader and each cell announced
 * separately is worse than silence. That makes the sentence the only accessible
 * form of this information, so it has to exist — and it is a required prop
 * rather than a convention, because a convention would be dropped by whoever
 * next adds a third caller.
 */
export function ConsistencyStrip({ days, caption, variant = 'bars', title }: Props) {
  const cells = (
    <div className={styles['cells']} aria-hidden="true">
      {days.map((day) => (
        <span
          key={day.dayKey}
          className={styles['cell']}
          data-band={variant === 'dots' ? Math.min(day.band, 1) : day.band}
        />
      ))}
    </div>
  )

  if (variant === 'band') {
    const first = days[0]
    return (
      <section className={styles['band']} aria-label={title} data-bleed>
        <div className={styles['bandHead']}>
          {title !== undefined && <h2 className={styles['bandTitle']}>{title}</h2>}
          <span className={styles['caption']}>{caption}</span>
        </div>
        {cells}
        <div className={styles['bandEnds']} aria-hidden="true">
          <span>{first === undefined ? '' : shortDay(first.dayKey)}</span>
          <span>Today</span>
        </div>
      </section>
    )
  }

  return (
    <div className={`${styles['strip']} ${styles[variant] ?? ''}`}>
      {cells}
      <span className={styles['caption']}>{caption}</span>
    </div>
  )
}
